import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { InstructionAttachment } from '@/components/feature/InstructionAttachmentThumbnails';
import {
  INSTRUCTIONS_SYNC_EVENT,
  type ManagementInstructionRecord,
} from '@/lib/instructionUtils';

const HIDDEN_INSTRUCTION_STATUSES = new Set(['closed', 'archived', 'deleted', 'cancelled']);

interface SubcontractorProfile {
  id: string;
  full_name: string | null;
  company_name: string | null;
}

interface UseManagementInstructionsListOptions {
  /** Specific project id, or `'all'` for every project. */
  projectId?: string;
}

export function useManagementInstructionsList(
  options: UseManagementInstructionsListOptions = {}
) {
  const { projectId } = options;
  const filterProjectId = projectId && projectId !== 'all' ? projectId : undefined;

  const [instructions, setInstructions] = useState<ManagementInstructionRecord[]>([]);
  const [subcontractors, setSubcontractors] = useState<SubcontractorProfile[]>([]);
  const [attachments, setAttachments] = useState<Record<string, InstructionAttachment[]>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const hasLoadedOnce = useRef(false);

  const load = useCallback(async () => {
    const isRefresh = hasLoadedOnce.current;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      let query = supabase
        .from('jphph_instructions')
        .select('*')
        .neq('instruction_type', 'change_order')
        .order('created_at', { ascending: false });

      if (filterProjectId) {
        query = query.eq('project_id', filterProjectId);
      }

      const { data, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;

      const instrs = ((data || []) as ManagementInstructionRecord[]).filter(
        (ins) => !HIDDEN_INSTRUCTION_STATUSES.has(String(ins.status || '').toLowerCase())
      );
      setInstructions(instrs);

      const subIds = [...new Set(instrs.map((i) => i.to_user_id).filter(Boolean))] as string[];
      if (subIds.length > 0) {
        const { data: profs } = await supabase
          .from('jphph_profiles')
          .select('id, full_name, company_name')
          .in('id', subIds);
        setSubcontractors((profs || []) as SubcontractorProfile[]);
      } else {
        setSubcontractors([]);
      }

      if (instrs.length > 0) {
        const instructionIds = instrs.map((i) => i.id);
        const { data: filesData } = await supabase
          .from('jphph_project_files')
          .select('id, file_name, file_url, file_type, photo_type, caption, instruction_id, change_order_id, storage_bucket, storage_path')
          .eq('is_deleted', false)
          .or(instructionIds.map((id) => `instruction_id.eq.${id}`).join(','));

        const grouped: Record<string, InstructionAttachment[]> = {};
        (filesData || []).forEach((f) => {
          const row = f as {
            id: string;
            file_url: string;
            file_name: string;
            file_type: string | null;
            photo_type: string | null;
            caption: string | null;
            instruction_id: string | null;
            change_order_id: string | null;
            storage_bucket: string | null;
            storage_path: string | null;
          };
          const key = row.instruction_id || row.change_order_id || '';
          if (!key) return;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push({
            id: row.id,
            file_url: row.file_url,
            file_name: row.file_name,
            file_type: row.file_type,
            photo_type: row.photo_type,
            caption: row.caption,
            storage_bucket: row.storage_bucket,
            storage_path: row.storage_path,
          });
        });
        setAttachments(grouped);
      } else {
        setAttachments({});
      }

      hasLoadedOnce.current = true;
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterProjectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId?: string }>).detail;
      if (
        filterProjectId &&
        detail?.projectId &&
        detail.projectId !== filterProjectId
      ) {
        return;
      }
      load();
    };
    window.addEventListener(INSTRUCTIONS_SYNC_EVENT, handler);
    return () => window.removeEventListener(INSTRUCTIONS_SYNC_EVENT, handler);
  }, [load, filterProjectId]);

  return {
    instructions,
    subcontractors,
    attachments,
    loading,
    refreshing,
    error,
    reload: load,
    hasData: instructions.length > 0,
  };
}
